/**
 * useFamilyMembers — the board's per-member filter chips.
 *
 * Reads GET /api/members/basic, the same safe-field projection the login
 * profile grid uses (explicit id/name/short_name/role/color_token — never PIN
 * hashes or dietary data). Archived members are already excluded server-side.
 *
 * Returns `[]` rather than throwing on failure: the chips are a refinement, so
 * a flaky fetch should cost the filter row, never the board itself. The 404
 * "no_members / onboarding_required" envelope collapses to the same empty list.
 */
import { useQuery } from '@tanstack/react-query';

export interface BasicMember {
  id: string;
  name: string;
  short_name: string;
  role: 'admin' | 'teen' | 'child';
  color_token: string;
}

export function useFamilyMembers(enabled = true) {
  return useQuery<BasicMember[]>({
    enabled,
    queryKey: ['members-basic'],
    queryFn: async () => {
      const r = await globalThis.fetch('/api/members/basic', {
        credentials: 'same-origin',
      });
      if (!r.ok) return [];
      const body = (await r.json()) as { members?: BasicMember[] };
      return body.members ?? [];
    },
    // The roster changes when a parent adds or archives someone — rare enough
    // that a long window is right, and MemberAdminSection invalidates on write.
    staleTime: 5 * 60_000,
    retry: false,
  });
}
